// Success toasts: the fact, stated plainly, out of the way.
//
//   showSuccessToast('Lesson scheduled · Tue 4:00 PM with Sarah')
//   showSuccessToast('Contact added', { actionLabel: 'View', actionHref: '/leads/12' })
//   toastAfterReload('Package added')  // for flows that must reload; the toast
//                                     // shows on the next page via sessionStorage
//
// Plain DOM on purpose: every page mounts its own React tree with its own
// style seed, and success often fires while the modal that owned the flow is
// unmounting. A body-level element owes nothing to any of that. Imported for
// its side effect in the Header entry, so the pending-toast flush runs on
// every page.

const PENDING_KEY = 'swyvel_pending_toast';
const STYLE_ID = 'swyvel-toast-style';

const CSS = `
#swyvel-toasts {
  position: fixed; left: 0; right: 0; bottom: 24px; z-index: 2000;
  display: flex; flex-direction: column-reverse; align-items: center; gap: 10px;
  pointer-events: none; padding: 0 16px;
}
.swyvel-toast {
  pointer-events: auto; display: flex; align-items: center; gap: 10px;
  background: #1a1a1a; color: #fff; border-radius: 999px;
  padding: 12px 22px; max-width: 92vw; box-sizing: border-box;
  font-family: 'Nunito', 'Varela Round', sans-serif; font-size: 14px; line-height: 1.4;
  box-shadow: 0 12px 32px rgba(0,0,0,.28);
  cursor: pointer;
  animation: swyvel-toast-in .32s cubic-bezier(.22,1,.36,1) both;
}
.swyvel-toast.swyvel-toast-out { animation: swyvel-toast-out .24s ease both; }
.swyvel-toast svg { flex-shrink: 0; animation: swyvel-toast-pop .4s cubic-bezier(.34,1.56,.64,1) .12s both; }
.swyvel-toast a {
  color: #7CB8FF; text-decoration: none; font-weight: 600; margin-left: 4px; white-space: nowrap;
}
.swyvel-toast a:hover { text-decoration: underline; }
@keyframes swyvel-toast-in { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
@keyframes swyvel-toast-out { from { opacity: 1; } to { opacity: 0; transform: translateY(8px); } }
@keyframes swyvel-toast-pop { from { transform: scale(0); } to { transform: scale(1); } }
@media (prefers-reduced-motion: reduce) {
  .swyvel-toast, .swyvel-toast svg { animation: swyvel-toast-fade .2s ease both; }
  .swyvel-toast.swyvel-toast-out { animation: swyvel-toast-fade .2s ease reverse both; }
  @keyframes swyvel-toast-fade { from { opacity: 0; } to { opacity: 1; } }
}
`;

const CHECK_SVG =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="10" fill="#6EE7B7"/>' +
  '<path d="M7.5 12.5l3 3 6-6.5" stroke="#065F46" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
  '</svg>';

const ensureSetup = () => {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }
  let container = document.getElementById('swyvel-toasts');
  if (!container) {
    container = document.createElement('div');
    container.id = 'swyvel-toasts';
    container.setAttribute('role', 'status');
    container.setAttribute('aria-live', 'polite');
    document.body.appendChild(container);
  }
  return container;
};

export const showSuccessToast = (message, { actionLabel, actionHref, duration = 4200 } = {}) => {
  if (!message) return;
  const container = ensureSetup();

  const toast = document.createElement('div');
  toast.className = 'swyvel-toast';
  toast.innerHTML = CHECK_SVG;

  const text = document.createElement('span');
  text.textContent = message;
  toast.appendChild(text);

  if (actionLabel && actionHref) {
    const link = document.createElement('a');
    link.href = actionHref;
    link.textContent = actionLabel;
    link.addEventListener('click', (e) => e.stopPropagation());
    toast.appendChild(link);
  }

  let removed = false;
  const remove = () => {
    if (removed) return;
    removed = true;
    toast.classList.add('swyvel-toast-out');
    setTimeout(() => toast.remove(), 300);
  };
  toast.addEventListener('click', remove);
  setTimeout(remove, duration);

  container.appendChild(toast);
  return remove;
};

// For flows that genuinely must reload: stash the toast, reload, and the
// flush below shows it on the fresh page. Replaces the OK-gated reload.
export const toastAfterReload = (message, opts = {}) => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ message, ...opts }));
  } catch (e) {
    // Storage unavailable (private mode quota etc.) — the reload still works,
    // the toast is just skipped.
  }
};

const flushPendingToast = () => {
  let pending;
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;
    sessionStorage.removeItem(PENDING_KEY);
    pending = JSON.parse(raw);
  } catch (e) {
    return;
  }
  if (!pending || !pending.message) return;
  // Small delay so the page paints first and the toast reads as a response.
  setTimeout(() => showSuccessToast(pending.message, pending), 400);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', flushPendingToast);
} else {
  flushPendingToast();
}

export default showSuccessToast;
